import { useState, useMemo, useEffect } from "react";
import { useQAPosts, useCreateQAPost, useToggleQAPostUpvote, useCreateQAComment, useQAPost, useQAComments } from "../hooks/queries/useQA";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

const useQAFacade = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [scopeFilter, setScopeFilter] = useState("public");
    const [newQuestion, setNewQuestion] = useState({ title: "", tags: "", scope: "public", content: "" });
    const [replyText, setReplyText] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [frozenPosts, setFrozenPosts] = useState([]);

    const { data: posts, isLoading: loadingPosts } = useQAPosts();
    const { data: selectedPostData } = useQAPost(selectedQuestion?._id);
    const { data: comments } = useQAComments(selectedQuestion?._id);

    const { user } = useSelector(state => state.user);

    const createPostMutation = useCreateQAPost();
    const upvoteMutation = useToggleQAPostUpvote();
    const createCommentMutation = useCreateQAComment();

    useEffect(() => {
        if (transformedPosts.length > 0) {
            setFrozenPosts(transformedPosts);
        }
    }, [refreshFlag, scopeFilter, searchTerm, posts === null]);

    // Transform posts data to match UI expectations
    const transformedPosts = useMemo(() => {
        if (!posts) return [];

        const filtered = posts
            .filter(post => post._id && typeof post._id === 'string' && post._id.length > 10 && post._id !== 'undefined') // Strict validation
            .map(post => ({
                _id: post._id,
                question: post.title,
                author: post.author?.name || "Anonymous",
                scope: "public", // For now, all are public
                tags: post.tags || [],
                upvotes: post.upvoteCount || 0,
                isupvoted: user ? post.upvotes?.some(upvoter => upvoter?.toString() === user._id?.toString()) : false,
                commentCount: post.commentCount || 0,
                replies: [], // We'll load comments separately
                time: new Date(post.createdAt).toLocaleString(),
                content: post.content,
            }));

        return filtered;
    }, [posts]);

    // Transform selected post with comments
    const transformedSelectedQuestion = useMemo(() => {
        if (!selectedPostData || !selectedQuestion) return null;

        const post = selectedPostData.post;
        const postComments = selectedPostData.comments || [];

        return {
            _id: post._id,
            question: post.title,
            author: post.author?.name || "Anonymous",
            scope: "public",
            tags: post.tags || [],
            upvotes: post.upvoteCount || 0,
            replies: postComments.map(comment => ({
                id: comment._id,
                text: comment.content,
                user: comment.author?.name || "Anonymous",
                time: new Date(comment.createdAt).toLocaleString(),
            })),
            time: new Date(post.createdAt).toLocaleString(),
            content: post.content,
        };
    }, [selectedPostData, selectedQuestion]);

    // Filter posts based on scope and search
    const filteredQuestions = useMemo(() => {
        let result = frozenPosts;

        // Filter by scope (for now all are public)
        result = result.filter(q => q.scope === scopeFilter);

        // Filter by search term
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(q =>
                q.question.toLowerCase().includes(lowerSearch) ||
                q.tags.some(tag => tag.toLowerCase().includes(lowerSearch)) ||
                q.author.toLowerCase().includes(lowerSearch)
            );
        }

        return result;
    }, [frozenPosts, scopeFilter, searchTerm]);

    const handlePostQuestion = async (e) => {
        e.preventDefault();
        if (!newQuestion.title.trim()) return;

        try {
            const tags = newQuestion.tags
                .split(",")
                .map(t => t.trim())
                .filter(t => t !== "");

            const postData = {
                title: newQuestion.title,
                content: newQuestion.content || newQuestion.title,
                tags,
            };

            await createPostMutation.mutateAsync(postData);

            setIsModalOpen(false);
            setNewQuestion({ title: "", tags: "", scope: scopeFilter, content: "" });
            toast.success("Post created successfully!");
        } catch (error) {
            toast.error("Failed to create post");
        }
    };

    const handlePostReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim() || !selectedQuestion) return;

        try {
            await createCommentMutation.mutateAsync({
                postId: selectedQuestion._id,
                content: replyText,
            });

            setReplyText("");
            toast.success("Reply posted successfully!");
        } catch (error) {
            toast.error("Failed to post reply");
        }
    };

    const handleUpvote = async (e, postId) => {
        e.stopPropagation();


        if (!postId || postId === 'undefined' || typeof postId !== 'string' || postId.length < 10) return;

        // 2. OPTIMISTIC UPDATE: Update the "frozen" UI instantly
        setFrozenPosts(prevPosts => 
            prevPosts.map(post => {
                if (post._id === postId) {
                    const isNowUpvoted = !post.isupvoted;
                    return {
                        ...post,
                        isupvoted: isNowUpvoted,
                        // Increment or decrement based on current state
                        upvotes: isNowUpvoted ? post.upvotes + 1 : post.upvotes - 1
                    };
                }
                return post;
            })
        );
        
        try {
            await upvoteMutation.mutateAsync({ postId });
        } catch (error) {
            toast.error("Failed to toggle upvote");
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    return {
        isModalOpen,
        setIsModalOpen,
        selectedQuestion: transformedSelectedQuestion,
        setSelectedQuestion,
        scopeFilter,
        setScopeFilter,
        newQuestion,
        setNewQuestion,
        replyText,
        setReplyText,
        searchTerm,
        setSearchTerm,
        filteredQuestions,
        loadingPosts,
        handlePostQuestion,
        handlePostReply,
        handleUpvote,
        handleSearch,
    };
};

export default useQAFacade;
